import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { formsAPI, packageAPI, authAPI } from '../services/api';
import toast from 'react-hot-toast';
import { proratedGB } from '../utils/prorate';
import { useAuthStore, UserRole } from '../stores/authStore';

// Utility function to format numbers with commas
const formatNumber = (num: number): string => {
  return num.toLocaleString('en-US');
};

const POOL_CUSTOMERS_REFETCH_INTERVAL_MS = 60_000;

interface ServiceForm {
  id: number;
  customerName: string;
  customerId?: string;
  serviceLine: string;
  kitNumber?: string;
  oldServiceLine?: string;
  packageLimit: number;
  dataTopup?: number;
  oldPackage?: number;
  // Full package cap the current packageLimit was prorated from (set when a
  // prorated amount was chosen mid-month). Null/undefined = not prorated.
  proratedFromCapGB?: number | null;
  currentUsage: number;
  usagePercentage?: number;
  notified75?: boolean;
  notified100?: boolean;
  status: string;
  monitorStatus?: string;
  activationDate?: string;
  lastChecked: string;
  accountManager?: string;
  // Nullable: a form submitted via a service account for an email with no
  // DataPool Monitor account has no linked owner (see submittedByEmail).
  owner?: {
    id: number;
    username: string;
    email: string;
  } | null;
  submittedByEmail?: string;
  isActive?: boolean;
  isCapped?: boolean;
}

interface FormData {
  customerName: string;
  customerId?: string;
  serviceLine: string;
  kitNumber?: string;
  oldServiceLine?: string;
  packageLimit: number;
  dataTopup?: number;
  oldPackage?: number;
  // Full package cap the packageLimit was prorated from; sent explicitly (use
  // null when not prorated) so the backend clears any stale marker.
  proratedFromCapGB?: number | null;
  activationDate?: string;
  accountManager?: string;
  isActive: boolean;
  isCapped: boolean;
}

// --- Audit Trail Types ---
interface FormAudit {
  id: number;
  fieldName: string;
  oldValue: string;
  newValue: string;
  timestamp: string;
  user: {
    id: number;
    username: string;
    email: string;
  };
}

interface BulkImportPreviewRow {
  rowNumber: number;
  data: FormData & {
    oldServiceLine?: string;
    oldPackage?: number;
    isCapped: boolean;
  };
  errors: string[];
}

interface BulkImportPreview {
  formType?: 'transfer' | 'regular';
  sheetName: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  rows: BulkImportPreviewRow[];
}

// --- Bulk Edit types (update existing forms from a sheet - Admin only) ---
interface BulkEditChange {
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
}

interface BulkEditPreviewRow {
  rowNumber: number;
  identifierType: 'serviceLine' | 'kitNumber' | null;
  identifierValue: string;
  matchedFormId?: number;
  matchedServiceLine?: string;
  matchedCustomerName?: string;
  data: {
    rowNumber: number;
    identifierType: 'serviceLine' | 'kitNumber' | null;
    identifierValue: string;
    values: Record<string, unknown>;
  };
  changes: BulkEditChange[];
  errors: string[];
}

interface BulkEditPreview {
  sheetName: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  rows: BulkEditPreviewRow[];
}

const Forms: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [showFormTypeModal, setShowFormTypeModal] = useState(false);
  const [formType, setFormType] = useState<'transfer' | 'regular' | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [editingForm, setEditingForm] = useState<ServiceForm | null>(null);
  const [filterOption, setFilterOption] = useState<string>('all');
  const [formData, setFormData] = useState<FormData>({
    customerName: '',
    customerId: '',
    serviceLine: '',
    kitNumber: '',
    oldServiceLine: '',
    packageLimit: 0,
    dataTopup: 0,
    oldPackage: 0,
    proratedFromCapGB: null,
    activationDate: '',
    accountManager: '',
    isActive: true,
    isCapped: true
  });

  const [prorated, setProrated] = useState<number | null>(null);
  const [selectedCappedPackage, setSelectedCappedPackage] = useState<number | null>(null);
  const [showProrateModal, setShowProrateModal] = useState(false);
  const [pendingPackage, setPendingPackage] = useState<{ cap: number; prorated: number; full: number } | null>(null);
  const [auditLogs, setAuditLogs] = useState<FormAudit[]>([]);
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [resetTopupModalOpen, setResetTopupModalOpen] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [formToDeactivate, setFormToDeactivate] = useState<ServiceForm | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false);
  const [formToReactivate, setFormToReactivate] = useState<ServiceForm | null>(null);
  const [isReactivating, setIsReactivating] = useState(false);
  const [isPackageLimitEditable, setIsPackageLimitEditable] = useState(false);
  const [accountManagers, setAccountManagers] = useState<{id: number, username: string, email: string}[]>([]);
  const [auditTargetForm, setAuditTargetForm] = useState<ServiceForm | null>(null);
  const [lastPageRefreshAt, setLastPageRefreshAt] = useState<Date | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [bulkImportPreview, setBulkImportPreview] = useState<BulkImportPreview | null>(null);
  const [bulkImportFileName, setBulkImportFileName] = useState('');
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditPreview, setBulkEditPreview] = useState<BulkEditPreview | null>(null);
  const [bulkEditFileName, setBulkEditFileName] = useState('');
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [search]);

  const backendSavedViewMap = React.useMemo<Record<string, string | undefined>>(() => ({
    all: undefined,
    'near-cap': 'near-cap',
    'at-cap': 'at-cap',
    'unassigned-manager': 'unassigned-manager',
    'inactive-operational': 'inactive-operational',
  }), []);

  const backendThreshold = filterOption === '75' ? 75 : undefined;
  const backendFilter =
    filterOption === 'optedin' || filterOption === 'optedout' || filterOption === 'active' || filterOption === 'inactive'
      ? filterOption
      : undefined;

  const formsQuery = useQuery(
    ['forms', filterOption, debouncedSearch, selectedCustomer, currentPage],
    () =>
      formsAPI.getFormsPage({
        view: backendSavedViewMap[filterOption],
        threshold: backendThreshold,
        filter: backendFilter as 'optedin' | 'optedout' | 'active' | 'inactive' | undefined,
        search: debouncedSearch || undefined,
        customerName: selectedCustomer.trim() || undefined,
        page: currentPage,
        limit: 100,
      }),
    {
      enabled: Boolean(user?.email),
      staleTime: 0,
      cacheTime: 10 * 60 * 1000,
      keepPreviousData: true,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      refetchInterval: POOL_CUSTOMERS_REFETCH_INTERVAL_MS,
      retry: 1,
    }
  );

  const formsResponse = formsQuery.data;
  const formsLoading = formsQuery.isLoading;
  const formsIsError = formsQuery.isError;
  const formsError = formsQuery.error;
  const refetchForms = formsQuery.refetch;
  const loadedForms = formsResponse?.data || [];

  // Update the "last refreshed" timestamp whenever react-query receives new data,
  // instead of inside the deprecated onSuccess callback which can trigger loops.
  useEffect(() => {
    if (formsQuery.dataUpdatedAt) {
      setLastPageRefreshAt(new Date(formsQuery.dataUpdatedAt));
    }
  }, [formsQuery.dataUpdatedAt]);

  useEffect(() => {
    if (formsIsError && formsError) {
      console.error('Error fetching forms:', formsError);
      const status = (formsError as any)?.response?.status;
      if (status !== 401 && status !== 403) {
        toast.error('Failed to load forms');
      }
    }
  }, [formsIsError, formsError]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterOption, selectedCustomer]);

  // React Query for packages
  const { data: packagesResponse } = useQuery('packages', packageAPI.list);
  const packages = useMemo(() => {
    const data = packagesResponse as any;
    if (Array.isArray(data)) return data as { id: number; capGB: number }[];
    if (data && Array.isArray(data.data)) return data.data as { id: number; capGB: number }[];
    return [];
  }, [packagesResponse]);

  // Fetch Account Manager users
  useEffect(() => {
    fetchAccountManagers();
  }, []);

  const fetchAccountManagers = async () => {
    try {
      const users = await authAPI.getAllUsers();
      const accountManagerUsers = users.filter((user: any) => user.role === 'account_manager');
      setAccountManagers(accountManagerUsers);
    } catch (error) {
      console.error('Error fetching account managers:', error);
    }
  };

  // Mutation for updating forms
  const updateFormMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => formsAPI.updateForm(id, data),
    {
      onSuccess: (updatedForm, variables) => {
        // Update cache immediately with the fresh data from server
        queryClient.setQueryData(['forms', filterOption], (oldData: any) => {
          if (!oldData) return oldData;
          
          // Handle both array and object responses (some APIs return {data: [...], total: x})
          const formsArray = Array.isArray(oldData) ? oldData : oldData.data || [];
          
          if (!Array.isArray(formsArray)) {
            return oldData;
          }
          
          // Remove messages from updatedForm before updating cache (messages is not part of form data)
          const { messages, ...formData } = updatedForm;
          
          const updatedArray = formsArray.map((form: any) => 
            form.id === parseInt(variables.id) ? { ...form, ...formData } : form
          );
          
          // Return in the same structure as oldData
          return Array.isArray(oldData) ? updatedArray : { ...oldData, data: updatedArray };
        });
        // Refresh active form queries immediately so the screen stays aligned
        // with the latest backend state after mutations.
        queryClient.invalidateQueries(['forms']);
        
        // Show custom messages if provided, otherwise show default success message
        if (updatedForm.messages && Array.isArray(updatedForm.messages) && updatedForm.messages.length > 0) {
          // Show each message as a separate toast
          updatedForm.messages.forEach((message: string) => {
            toast.success(message);
          });
        } else {
          toast.success('Service form updated successfully! 📝');
        }
        
        resetForm();
      },
      onError: (error: any) => {
        const message =
          error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')
            ? 'Update timed out. The change may have been applied—please refresh to confirm.'
            : error.response?.data?.message || error.response?.data?.detail || error.message || 'Update failed';
        toast.error(message);
      }
    }
  );

  // Mutation for creating forms
  const createFormMutation = useMutation(
    (data: any) => formsAPI.createForm(data),
    {
      onSuccess: (newForm) => {
        // Invalidate and refetch to get fresh data from server immediately.
        queryClient.invalidateQueries(['forms']);
        // Also update the active page cache immediately as fallback
        // (handles both array and paginated shapes).
        queryClient.setQueryData(['forms', filterOption], (oldData: any) => {
          if (!oldData) return oldData;

          // Old code assumed `oldData` is an array, but `getAllForms()` often returns:
          // { data: ServiceForm[], pagination: {...} }
          const existingArray = Array.isArray(oldData)
            ? oldData
            : (oldData?.data && Array.isArray(oldData.data) ? oldData.data : []);

          const exists = existingArray.some((f: any) => f.id === newForm.id);
          if (exists) return oldData;

          const nextArray = [...existingArray, newForm];
          return Array.isArray(oldData) ? nextArray : { ...oldData, data: nextArray };
        });
        toast.success('Service form created successfully! ✨');
        resetForm();
        void refetchForms();
      },
      onError: (error: any) => {
        const message =
          error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')
            ? 'Creation timed out. The record may have been created—please refresh to confirm.'
            : error.response?.data?.message || error.response?.data?.detail || error.message || 'Creation failed';
        toast.error(message);
      }
    }
  );

  const bulkPreviewMutation = useMutation(
    (file: File) => formsAPI.previewBulkImport(file, formType === 'regular' ? 'regular' : 'transfer'),
    {
      onSuccess: (preview: BulkImportPreview) => {
        setBulkImportPreview(preview);
        const label = preview.formType === 'regular' ? 'regular' : 'transfer';
        if (preview.invalidRows > 0) {
          toast.error(`${preview.invalidRows} row${preview.invalidRows === 1 ? '' : 's'} need attention before import`);
        } else {
          toast.success(`${preview.validRows} ${label} row${preview.validRows === 1 ? '' : 's'} ready to import`);
        }
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || error.message || 'Bulk import preview failed';
        toast.error(message);
      }
    }
  );

  const bulkCommitMutation = useMutation(
    (rows: any[]) => formsAPI.commitBulkImport(rows, formType === 'regular' ? 'regular' : 'transfer'),
    {
      onSuccess: (result) => {
        toast.success(result?.message || 'Forms imported successfully');
        queryClient.invalidateQueries(['forms']);
        setBulkImportPreview(null);
        setBulkImportFileName('');
        resetForm();
        void refetchForms();
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || error.message || 'Bulk import failed';
        toast.error(message);
      }
    }
  );

  const bulkEditPreviewMutation = useMutation(
    (file: File) => formsAPI.previewBulkEdit(file),
    {
      onSuccess: (preview: BulkEditPreview) => {
        setBulkEditPreview(preview);
        if (preview.invalidRows > 0) {
          toast.error(`${preview.invalidRows} row${preview.invalidRows === 1 ? '' : 's'} need attention before applying`);
        } else {
          const changedRows = preview.rows.filter((row) => row.changes.length > 0).length;
          toast.success(
            changedRows > 0
              ? `${changedRows} row${changedRows === 1 ? '' : 's'} have changes ready to apply`
              : 'No changes detected in this sheet'
          );
        }
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || error.message || 'Bulk edit preview failed';
        toast.error(message);
      }
    }
  );

  const bulkEditCommitMutation = useMutation(
    (rows: any[]) => formsAPI.commitBulkEdit(rows),
    {
      onSuccess: (result) => {
        toast.success(result?.message || 'Forms updated successfully');
        queryClient.invalidateQueries(['forms']);
        setBulkEditPreview(null);
        setBulkEditFileName('');
        setShowBulkEditModal(false);
        void refetchForms();
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || error.message || 'Bulk edit failed';
        toast.error(message);
      }
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate oldPackage and oldServiceLine for transfer forms
    if (!editingForm && formType === 'transfer') {
      if (!formData.oldPackage || formData.oldPackage <= 0) {
        toast.error('Old Package is required for Transfer forms');
        return;
      }
      if (!formData.oldServiceLine || formData.oldServiceLine.trim() === '') {
        toast.error('Old Service Line is required for Transfer forms');
        return;
      }
    }
    
      // Set isCapped based on form type if creating new form
      let isCappedValue = formData.isCapped;
      if (!editingForm) {
        // Regular form = Capped (true), Transfer form = Uncapped (false)
        isCappedValue = formType === 'regular' ? true : false;
      }
    
      const submitData = {
        ...formData,
        packageLimit: Number(formData.packageLimit),
        dataTopup: Number(formData.dataTopup || 0),
        // Sent explicitly (null when not prorated) so the backend clears any
        // stale proration marker when the limit changes.
        proratedFromCapGB: formData.proratedFromCapGB ?? null,
        oldPackage: formData.oldPackage ? Number(formData.oldPackage) : undefined,
        activationDate: formData.activationDate || undefined,
        oldServiceLine: formData.oldServiceLine || undefined,
        isActive: formData.isActive,
        isCapped: isCappedValue
      };

      if (editingForm) {
      updateFormMutation.mutate({ id: String(editingForm.id), data: submitData });
      } else {
      createFormMutation.mutate(submitData);
    }
  };

  const handleBulkFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const lowerName = file.name.toLowerCase();
    if (!validExtensions.some((extension) => lowerName.endsWith(extension))) {
      toast.error('Please upload an Excel or CSV file');
      return;
    }

    setBulkImportFileName(file.name);
    setBulkImportPreview(null);
    bulkPreviewMutation.mutate(file);
  };

  const handleBulkImportCommit = () => {
    if (!bulkImportPreview || bulkImportPreview.invalidRows > 0) return;
    const rows = bulkImportPreview.rows.map((row) => row.data);
    bulkCommitMutation.mutate(rows);
  };

  const handleBulkEditFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const lowerName = file.name.toLowerCase();
    if (!validExtensions.some((extension) => lowerName.endsWith(extension))) {
      toast.error('Please upload an Excel or CSV file');
      return;
    }

    setBulkEditFileName(file.name);
    setBulkEditPreview(null);
    bulkEditPreviewMutation.mutate(file);
  };

  const handleBulkEditCommit = () => {
    if (!bulkEditPreview || bulkEditPreview.invalidRows > 0) return;
    const rows = bulkEditPreview.rows.map((row) => row.data);
    bulkEditCommitMutation.mutate(rows);
  };

  const resetBulkEditModal = () => {
    setShowBulkEditModal(false);
    setBulkEditPreview(null);
    setBulkEditFileName('');
  };

  const downloadBulkEditTemplate = () => {
    const headers = [
      'Service Line',
      'Kit Number',
      'Customer Name',
      'Customer ID',
      'Old Service Line',
      'Old Package',
      'Package Limit',
      'Data Topup',
      'Activation Date',
      'Account Manager',
      'Client Status',
      'Capped'
    ];
    // Sample shows the point of the sheet: identify by Service Line, leave
    // every field blank except the one you actually want to overwrite
    // (here, just Package Limit and Account Manager change).
    const sample = ['SL-NEW-001', '', '', '', '', '', '300', '', '', 'jane@example.com', '', ''];
    const csv = [headers, sample]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk-edit-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadBulkTemplate = () => {
    const isTransfer = formType !== 'regular';
    const headers = isTransfer
      ? [
          'Customer Name',
          'Customer ID',
          'Service Line',
          'Kit Number',
          'Old Service Line',
          'Old Package',
          'Package Limit',
          'Data Topup',
          'Activation Date',
          'Account Manager',
          'Client Status'
        ]
      : [
          'Customer Name',
          'Customer ID',
          'Service Line',
          'Kit Number',
          'Package Limit',
          'Data Topup',
          'Activation Date',
          'Account Manager',
          'Client Status'
        ];
    const sample = isTransfer
      ? [
          'Acme Mining',
          'CUST-001',
          'SL-NEW-001',
          'KIT-001',
          'SL-OLD-001',
          '100',
          '250',
          '0',
          new Date().toISOString().split('T')[0],
          '',
          'Active'
        ]
      : [
          'Acme Mining',
          'CUST-001',
          'SL-NEW-001',
          'KIT-001',
          '250',
          '0',
          new Date().toISOString().split('T')[0],
          '',
          'Active'
        ];
    const csv = [headers, sample]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = isTransfer ? 'transfer-bulk-upload-template.csv' : 'regular-bulk-upload-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleEdit = (form: ServiceForm) => {
    setEditingForm(form);
    setIsPackageLimitEditable(false); // Reset to locked state when editing
    setFormData({
      customerName: form.customerName,
      customerId: form.customerId || '',
      serviceLine: form.serviceLine,
      kitNumber: form.kitNumber || '',
      oldServiceLine: form.oldServiceLine || '',
      packageLimit: form.packageLimit,
      dataTopup: form.dataTopup || 0,
      oldPackage: form.oldPackage || 0,
      proratedFromCapGB: form.proratedFromCapGB ?? null,
      activationDate: form.activationDate?.split('T')[0] || '',
      accountManager: form.accountManager || '',
      isActive: form.isActive || true,
      isCapped: form.isCapped !== undefined ? form.isCapped : true
    });
    
    // Check if the form's package limit matches any capped package
    // Safety check: ensure packages is an array before calling .find()
    if (Array.isArray(packages) && packages.length > 0) {
      const matchingPackage = packages.find(pkg => pkg.capGB === form.packageLimit);
      if (matchingPackage) {
        setSelectedCappedPackage(matchingPackage.capGB);
        // Check if this is a prorated amount
        const currentProrated = proratedGB(matchingPackage.capGB);
        if (Math.abs(form.packageLimit - currentProrated) < 0.1) {
          setProrated(currentProrated);
        } else {
          setProrated(null);
        }
      } else {
        setSelectedCappedPackage(null);
        setProrated(null);
      }
    } else {
      setSelectedCappedPackage(null);
      setProrated(null);
    }
    
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      customerName: '',
      customerId: '',
      serviceLine: '',
      kitNumber: '',
      oldServiceLine: '',
      packageLimit: 0,
      dataTopup: 0,
      oldPackage: 0,
      proratedFromCapGB: null,
      activationDate: '',
      accountManager: '',
      isActive: true,
      isCapped: true
    });
    setEditingForm(null);
    setIsPackageLimitEditable(false); // Reset to locked state when closing
    setProrated(null);
    setSelectedCappedPackage(null);
    setBulkImportPreview(null);
    setBulkImportFileName('');
    setShowModal(false);
    setFormType(null);
  };

  const formsArray = loadedForms;

  // Export all service lines to Excel via backend endpoint
  const handleExport = async () => {
    try {
      toast.loading('Preparing export... This may take a moment for large datasets.', { duration: 3000 });
      
      // Use the new all-service-lines export endpoint
      const exportFilters = {
        search: debouncedSearch,
        filterOption: filterOption
      };
      
      const blob = await formsAPI.exportAllServiceLines(exportFilters);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all_service_lines_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('All service lines exported successfully 📄');
    } catch (error: any) {
      console.error('Export failed:', error);
      if (error.response?.status === 422) {
        toast.error('No forms found to export');
      } else if (error.response?.status === 429) {
        toast.error('Rate limit exceeded. Please wait a moment and try again.');
      } else {
        toast.error('Failed to export forms. Please try again.');
      }
    }
  };

  // Reset all topup data to 0
  const handleResetTopup = async () => {
    try {
      toast.loading('Resetting topup data...', { duration: 2000 });
      
      const result = await formsAPI.resetTopup();
      
      toast.success(result.message || `Successfully reset topup for ${result.updatedCount} forms`);
      
      // Refresh all form-related queries immediately
      queryClient.invalidateQueries(['forms']);
      void refetchForms();
      
      // Close the modal
      setResetTopupModalOpen(false);
      
    } catch (error: any) {
      console.error('Reset topup error:', error);
      toast.error(error.response?.data?.message || 'Failed to reset topup data');
    }
  };

  // (CSV helper removed — export is handled via backend XLSX endpoint)

  // View details handler
  const handleViewDetails = () => {
    // Navigate to the 100% usage page
    window.location.href = '/usage-100-percent';
  };

  const getUsageColor = (usage: number, limit: number) => {
    const percentage = (usage / limit) * 100;
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 75) return 'text-yellow-600';
    if (percentage >= 50) return 'text-orange-600';
    return 'text-green-600';
  };

  const getMonitorColor = (status?: string) => {
    if (status === 'Monitored') return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  const displayedForms = [...formsArray].sort((left, right) => {
    const leftActive = Boolean(left.isActive);
    const rightActive = Boolean(right.isActive);

    if (leftActive !== rightActive) {
      return leftActive ? -1 : 1;
    }

    return String(left.customerName || '').localeCompare(String(right.customerName || ''), undefined, {
      sensitivity: 'base',
    });
  });

  const stats = formsResponse?.stats;
  const pagination = formsResponse?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalRecords = pagination?.total || 0;
  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * 100 + 1;
  const rangeEnd = totalRecords === 0 ? 0 : Math.min(currentPage * 100, totalRecords);

  // View audit trail
  const handleAudit = async (form: ServiceForm) => {
    try {
      const logs = await formsAPI.getFormAudit(String(form.id));
      setAuditLogs(logs);
      setAuditTargetForm(form);
      setAuditModalOpen(true);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load audit trail');
    }
  };

  // Open deactivate confirmation modal
  const handleDeactivateClick = (form: ServiceForm) => {
    setFormToDeactivate(form);
    setDeactivateModalOpen(true);
  };

  // Deactivate service line
  const handleDeactivate = async () => {
    if (!formToDeactivate) return;

    setIsDeactivating(true);
    try {
      toast.loading('Deactivating service line...', { id: 'deactivate' });
      await formsAPI.deactivateServiceLine(formToDeactivate.id, true);
      toast.success(`Service line ${formToDeactivate.serviceLine} deactivated successfully`, { id: 'deactivate' });
      queryClient.invalidateQueries(['forms']);
      void refetchForms();
      // Close modal
      setDeactivateModalOpen(false);
      setFormToDeactivate(null);
    } catch (error: any) {
      console.error('Deactivate error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to deactivate service line';
      toast.error(errorMessage, { id: 'deactivate' });
    } finally {
      setIsDeactivating(false);
    }
  };

  // Open reactivate confirmation modal
  const handleReactivateClick = (form: ServiceForm) => {
    setFormToReactivate(form);
    setReactivateModalOpen(true);
  };

  // Reactivate service line (productReferenceId is fetched automatically from Starlink API)
  const handleReactivate = async () => {
    if (!formToReactivate) {
      return;
    }

    setIsReactivating(true);
    try {
      toast.loading('Fetching service line details and reactivating...', { id: 'reactivate' });
      await formsAPI.reactivateServiceLine(formToReactivate.id);
      toast.success(`Service line ${formToReactivate.serviceLine} reactivated successfully`, { id: 'reactivate' });
      queryClient.invalidateQueries(['forms']);
      void refetchForms();
      // Close modal
      setReactivateModalOpen(false);
      setFormToReactivate(null);
    } catch (error: any) {
      console.error('Reactivate error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reactivate service line';
      toast.error(errorMessage, { id: 'reactivate' });
    } finally {
      setIsReactivating(false);
    }
  };

  if (formsLoading && loadedForms.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading service forms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_15%_20%,rgba(56,189,248,0.35),transparent_45%),radial-gradient(circle_at_85%_25%,rgba(167,139,250,0.28),transparent_55%)]" />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,0.12)]" />
              System online
            </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Pool Customers</h1>
            <p className="max-w-2xl text-white/70">
              Complete customer service management, monitoring status, exports, and operational actions.
            </p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-white/70">
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-users text-white/60"></i>
                {formatNumber(totalRecords)} customers
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-filter text-white/60"></i>
                Filter: {filterOption}
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-clock text-white/60"></i>
                Updated: {lastPageRefreshAt ? lastPageRefreshAt.toLocaleTimeString() : 'Loading'}
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-arrows-rotate text-white/60"></i>
                Auto-refresh: every {POOL_CUSTOMERS_REFETCH_INTERVAL_MS / 1000}s
              </span>
              </div>
              </div>

          <div className="flex flex-wrap gap-3">
            {user && (user.role === UserRole.USER || user.role === UserRole.ADMIN || user.role === UserRole.SUPER_ADMIN) && (
              <button
                onClick={() => navigate('/data-transfer')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-5 py-3 font-semibold text-white shadow-lg transition-all hover:bg-white/15"
              >
                <i className="fas fa-exchange-alt"></i>
                Data transfer
              </button>
            )}
            <button
              onClick={handleViewDetails}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-5 py-3 font-semibold text-white shadow-lg transition-all hover:bg-white/15"
            >
              <i className="fas fa-eye"></i>
              View details
            </button>
          </div>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search Input */}
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              className="w-full rounded-2xl bg-gray-50 px-5 py-3 pl-12 text-gray-900 placeholder-gray-400 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              placeholder="Search customers, service lines, kit numbers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              className="rounded-2xl bg-gray-50 px-4 py-3 font-semibold text-gray-900 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              placeholder="Customer filter..."
            />
            {/* Filter Dropdown */}
            <select
              value={filterOption}
              onChange={(e) => setFilterOption(e.target.value)}
              className="rounded-2xl bg-gray-50 px-4 py-3 font-semibold text-gray-900 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
            >
              <option value="all">All Records</option>
              <option value="optedin">Opted In</option>
              <option value="optedout">Opted Out</option>
              <option value="75">≥ 75% Usage</option>
              <option value="near-cap">Saved View: Near Cap</option>
              <option value="at-cap">Saved View: At/Over 100%</option>
              <option value="unassigned-manager">Saved View: No Account Manager</option>
              <option value="inactive-operational">Saved View: Recently Touched Inactive</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            
            {/* Action Buttons */}
            {!user?.hasAuditAccess && (user?.role === UserRole.USER || user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN) && (
              <button
                onClick={() => setResetTopupModalOpen(true)}
                className="bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-3 rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <i className="fas fa-undo mr-2"></i>
                Reset TopUp
              </button>
            )}

            {!user?.hasAuditAccess && (
              <button
                onClick={handleExport}
                className="bg-gradient-to-r from-emerald-600 to-green-600 text-white px-6 py-3 rounded-2xl hover:brightness-110 transition-all duration-200 font-semibold shadow-lg"
              >
                <i className="fas fa-file-excel mr-2"></i>
                Export
              </button>
            )}

            {!user?.hasViewOnlyAccess && !user?.hasAuditAccess && (
              <button
                onClick={() => {
                  if (editingForm) {
                    setShowModal(true);
                  } else {
                    setShowFormTypeModal(true);
                  }
                }}
                className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-6 py-3 rounded-2xl hover:brightness-110 transition-all duration-200 font-semibold shadow-lg"
              >
                <i className="fas fa-plus mr-2"></i>
                New Form
              </button>
            )}

            {/* Bulk edit updates existing forms in place from a sheet - the
                blast radius (silently overwriting many records at once) is
                why it's restricted to Admin/Super Admin, unlike bulk create. */}
            {user?.hasAdminAccess && (
              <button
                onClick={() => setShowBulkEditModal(true)}
                className="bg-gradient-to-r from-slate-700 to-slate-900 text-white px-6 py-3 rounded-2xl hover:brightness-110 transition-all duration-200 font-semibold shadow-lg"
              >
                <i className="fas fa-file-pen mr-2"></i>
                Bulk Edit
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(stats?.totalForms ?? displayedForms.length)}</p>
              <p className="text-gray-600 font-medium text-sm">{search || selectedCustomer ? 'Found' : 'Total'} Forms</p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg">
              <i className="fas fa-file-alt text-lg"></i>
            </div>
          </div>
          <div className="mt-3">
            <span className="text-gray-500 text-xs">Service records</span>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(stats?.activeForms ?? displayedForms.filter(f => f.isActive).length)}
              </p>
              <p className="text-gray-600 font-medium text-sm">Active</p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg">
              <i className="fas fa-check-circle text-lg"></i>
            </div>
          </div>
          <div className="mt-3 flex items-center">
            <div className="flex items-center text-green-600">
              <i className="fas fa-arrow-up text-xs mr-1"></i>
              <span className="text-xs font-medium">+5%</span>
            </div>
            <span className="text-gray-500 text-xs ml-2">this week</span>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(stats?.inactiveForms ?? displayedForms.filter(f => !f.isActive).length)}
              </p>
              <p className="text-gray-600 font-medium text-sm">Inactive</p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 text-white shadow-lg">
              <i className="fas fa-pause-circle text-lg"></i>
            </div>
          </div>
          <div className="mt-3">
            <span className="text-gray-500 text-xs">Suspended services</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(stats?.optedOutForms ?? displayedForms.filter(f => f.status.toLowerCase() === 'opted out').length)}
              </p>
              <p className="text-gray-600 font-medium text-sm">Opted Out</p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg">
              <i className="fas fa-user-times text-lg"></i>
            </div>
          </div>
          <div className="mt-3">
            <span className="text-gray-500 text-xs">Monitoring disabled</span>
          </div>
        </div>

        <div className="md:col-span-2 bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative">
          <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-lg absolute top-4 right-4">
            <i className="fas fa-chart-pie text-xl"></i>
          </div>
          <div className="pr-16">
            <div className="flex items-baseline space-x-2">
              <p className="text-4xl font-bold text-gray-900 whitespace-nowrap">
                {(stats?.totalUsageGB ?? displayedForms
                  .reduce((acc, f) => acc + f.currentUsage, 0))
                  .toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
              </p>
              <span className="text-xl font-bold text-gray-900">GB</span>
            </div>
            <p className="text-gray-600 font-medium mt-2">Total Usage</p>
            <p className="text-gray-500 text-sm mt-1">Data consumed</p>
          </div>
        </div>
      </div>

      {/* Modern Forms Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gray-50 px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-black text-gray-900">Pool Customers</h3>
              {displayedForms.length > 0 && (
                <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                  {totalRecords} records
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500">
              Showing {rangeStart}-{rangeEnd} of {totalRecords}
            </div>
          </div>
        </div>
        
        <div className="overflow-auto table-scroll" style={{ maxHeight: '600px' }}>
            <table className="forms-table w-full" style={{ minWidth: '1180px', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '18%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '24%' }} />
              <col style={{ width: '26%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '10%' }} />
            </colgroup>
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider align-top">
                    Customer Info
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider align-top">
                    Account Manager
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider align-top">
                    Service Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider align-top">
                    Package & Usage
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider align-top">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider align-top">
                    Monitor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider align-top">
                    Owner
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider align-top">
                    Actions
                  </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {displayedForms.map((form, index) => (
                <tr key={form.id} className={`border-b border-gray-100 hover:bg-gradient-to-r hover:from-violet-50 hover:to-purple-50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                                     <td className="px-4 py-4 align-top">
                     <div>
                       <div className="text-sm font-bold text-gray-900">
                         {form.customerName}
                       </div>
                       {form.customerId && (
                         <div className="text-sm text-gray-500">
                           ID: {form.customerId}
                         </div>
                       )}
                       {form.activationDate && (
                         <div className="text-xs text-gray-500 mt-2 flex items-center">
                           <i className="fas fa-calendar mr-1"></i>
                           Activated: {new Date(form.activationDate).toLocaleDateString()}
                         </div>
                       )}
                     </div>
                   </td>
                  <td className="px-4 py-4 align-top">
                    <div className="text-sm text-gray-900">
                      {form.accountManager ? (
                        <span className="font-medium">{form.accountManager}</span>
                      ) : (
                        <span className="text-gray-400 italic">Not assigned</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div>
                      <div className="text-sm font-semibold text-gray-900 flex items-center">
                        <i className="fas fa-satellite mr-2 text-blue-500 flex-shrink-0"></i>
                        <span className="break-all">{form.serviceLine}</span>
                      </div>
                      {form.kitNumber && (
                        <div className="text-sm text-gray-500 flex items-center mt-1">
                          <i className="fas fa-box mr-2 text-gray-400 flex-shrink-0"></i>
                          <span className="break-all">Kit: {form.kitNumber}</span>
                        </div>
                      )}
                      {form.oldServiceLine && (
                        <div className="text-xs text-gray-500 mt-1 flex items-center">
                          <i className="fas fa-arrow-left mr-1 flex-shrink-0"></i>
                          <span className="break-all">From: {form.oldServiceLine}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900">
                        {form.packageLimit} GB Package
                        {(form.dataTopup && Number(form.dataTopup) > 0) ? (
                          <span className="text-green-600"> + {form.dataTopup} GB Topup</span>
                        ) : ''}
                      </div>
                      {form.oldPackage && (
                        <div className="text-xs text-gray-500 whitespace-nowrap">
                          Upgraded from: {form.oldPackage} GB
                        </div>
                      )}
                      <div className="mt-2">
                        <div className={`text-sm font-bold ${getUsageColor(form.currentUsage, form.packageLimit + (form.dataTopup || 0))}`}>
                          {form.currentUsage} GB used
                        </div>
                        <div className="flex items-center mt-1">
                          <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className={`h-2 rounded-full ${
                                (form.currentUsage / (form.packageLimit + (form.dataTopup || 0))) * 100 >= 90 ? 'bg-red-500' :
                                (form.currentUsage / (form.packageLimit + (form.dataTopup || 0))) * 100 >= 75 ? 'bg-yellow-500' :
                                (form.currentUsage / (form.packageLimit + (form.dataTopup || 0))) * 100 >= 50 ? 'bg-orange-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min((form.currentUsage / (form.packageLimit + (form.dataTopup || 0))) * 100, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">
                            {(form.usagePercentage !== undefined ? form.usagePercentage : ((form.currentUsage / (form.packageLimit + (form.dataTopup || 0))) * 100)).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${form.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {form.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="flex flex-col items-start gap-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-lg ${getMonitorColor(form.monitorStatus)}`}>
                        {form.monitorStatus ?? 'Skipped'}
                      </span>
                      <div className="flex">
                        {form.notified100 ? (
                          <span className="inline-flex px-2 py-1 text-xs font-bold rounded-lg bg-red-100 text-red-800">100%</span>
                        ) : form.notified75 ? (
                          <span className="inline-flex px-2 py-1 text-xs font-bold rounded-lg bg-yellow-100 text-yellow-800">75%</span>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="text-sm font-medium text-gray-900">
                      {form.owner?.username || form.submittedByEmail || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="flex max-w-[7rem] flex-wrap items-center justify-center gap-1.5">
                      {!user?.hasViewOnlyAccess && !user?.hasAuditAccess && (
                        <button
                          onClick={() => handleEdit(form)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg p-2 text-violet-600 transition-all duration-200 hover:bg-violet-50 hover:text-violet-800 group"
                          title="Edit Form"
                        >
                          <i className="fas fa-edit text-base group-hover:scale-110 transition-transform"></i>
                        </button>
                      )}
                      <button
                        onClick={() => handleAudit(form)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg p-2 text-blue-600 transition-all duration-200 hover:bg-blue-50 hover:text-blue-800 group"
                        title="View Audit Trail"
                      >
                        <i className="fas fa-history text-base group-hover:scale-110 transition-transform"></i>
                      </button>
                      <button
                        onClick={() => navigate(`/forms/${form.id}/workspace`)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg p-2 text-slate-600 transition-all duration-200 hover:bg-slate-50 hover:text-slate-900 group"
                        title="Open Workspace"
                      >
                        <i className="fas fa-up-right-from-square text-base group-hover:scale-110 transition-transform"></i>
                      </button>
                      {(user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.USER) && (
                        <>
                          {form.isActive ? (
                            <button
                              onClick={() => handleDeactivateClick(form)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg p-2 text-red-600 transition-all duration-200 hover:bg-red-50 hover:text-red-800 group"
                              title="Deactivate Service Line"
                            >
                              <i className="fas fa-power-off text-base group-hover:scale-110 transition-transform"></i>
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivateClick(form)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg p-2 text-green-600 transition-all duration-200 hover:bg-green-50 hover:text-green-800 group"
                              title="Resume/Reactivate Service Line"
                            >
                              <i className="fas fa-play text-base group-hover:scale-110 transition-transform"></i>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalRecords > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-gray-600">
              Page {currentPage}/{totalPages} - forms {rangeStart}-{rangeEnd}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage <= 1}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages}
                className="rounded-lg bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
        
        {displayedForms.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="fas fa-file-alt text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">No forms found</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {search ? 'No forms match your search criteria. Try adjusting your search terms.' : 'Get started by creating your first service form.'}
            </p>
            {!search && (
              <button
                onClick={() => setShowModal(true)}
                className="text-violet-600 hover:text-violet-700 font-medium"
              >
                Create your first form →
              </button>
            )}
          </div>
        )}
      </div>

      {/* Bulk Edit Modal - updates existing forms in place from a sheet */}
      {showBulkEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto hide-scrollbar">
            <div className="p-8 relative">
              <button
                onClick={resetBulkEditModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>

              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Bulk Edit Existing Forms</h3>
                <p className="text-gray-600">
                  Update many existing customers at once from a spreadsheet.
                </p>
              </div>

              <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-800 text-white">
                        <i className="fas fa-file-upload"></i>
                      </span>
                      Upload sheet
                    </h4>
                    <p className="mt-2 text-sm text-gray-600">
                      Each row is matched to an existing form by <strong>Service Line</strong>; if that cell is blank, <strong>Kit Number</strong> is used instead. Every other column follows one rule: <strong>leave a cell blank to keep what's already there, or fill it in to overwrite it.</strong> Recognized columns: Service Line, Kit Number, Customer Name, Customer ID, Old Service Line, Old Package, Package Limit, Data Topup, Activation Date, Account Manager, Client Status, Capped.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={downloadBulkEditTemplate}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-100"
                    >
                      <i className="fas fa-download"></i>
                      Template
                    </button>
                    <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-slate-800 px-4 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-slate-900">
                      <i className="fas fa-upload"></i>
                      Upload sheet
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleBulkEditFileChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {(bulkEditPreviewMutation.isLoading || bulkEditFileName || bulkEditPreview) && (
                  <div className="mt-5 rounded-xl bg-white p-4 ring-1 ring-slate-100">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <div className="text-sm font-bold text-gray-900">
                          {bulkEditFileName || 'Preparing preview'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {bulkEditPreviewMutation.isLoading
                            ? 'Reading and matching rows...'
                            : bulkEditPreview
                              ? `${bulkEditPreview.sheetName} • ${bulkEditPreview.totalRows} row${bulkEditPreview.totalRows === 1 ? '' : 's'}`
                              : 'No preview loaded'}
                        </div>
                      </div>
                      {bulkEditPreview && (
                        <div className="flex flex-wrap gap-2 text-xs font-bold">
                          <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700">
                            Total: {bulkEditPreview.totalRows}
                          </span>
                          <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-700">
                            Valid: {bulkEditPreview.validRows}
                          </span>
                          <span className="rounded-full bg-red-100 px-3 py-1 text-red-700">
                            Errors: {bulkEditPreview.invalidRows}
                          </span>
                        </div>
                      )}
                    </div>

                    {bulkEditPreview && (
                      <div className="mt-4">
                        {/* Scrollable both ways so a large sheet (100+ rows, or many
                            error rows) can all be reviewed here, header pinned. */}
                        <div className="max-h-96 overflow-y-auto overflow-x-auto rounded-lg ring-1 ring-gray-100">
                          <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="sticky top-0 z-10 bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-bold text-gray-600">Row</th>
                                <th className="px-3 py-2 text-left font-bold text-gray-600">Matched By</th>
                                <th className="px-3 py-2 text-left font-bold text-gray-600">Customer</th>
                                <th className="px-3 py-2 text-left font-bold text-gray-600">Changes</th>
                                <th className="px-3 py-2 text-left font-bold text-gray-600">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                              {bulkEditPreview.rows.map((row) => (
                                <tr key={row.rowNumber}>
                                  <td className="px-3 py-2 font-semibold text-gray-700">{row.rowNumber}</td>
                                  <td className="px-3 py-2 text-gray-700">
                                    {row.identifierType === 'kitNumber' ? 'Kit Number' : 'Service Line'}: {row.identifierValue || '-'}
                                  </td>
                                  <td className="px-3 py-2 text-gray-700">
                                    {row.matchedCustomerName || '-'}
                                    {row.matchedServiceLine && (
                                      <div className="text-xs text-gray-400">{row.matchedServiceLine}</div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-gray-700">
                                    {row.changes.length === 0 ? (
                                      <span className="text-xs text-gray-400">No changes</span>
                                    ) : (
                                      <ul className="space-y-0.5 text-xs">
                                        {row.changes.map((change) => (
                                          <li key={change.field}>
                                            <span className="font-semibold text-gray-700">{change.label}:</span>{' '}
                                            <span className="text-gray-400 line-through">{change.oldValue || '(empty)'}</span>
                                            {' → '}
                                            <span className="font-semibold text-emerald-700">{change.newValue || '(empty)'}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    {row.errors.length === 0 ? (
                                      <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-700">Ready</span>
                                    ) : (
                                      <div className="max-w-xs text-xs font-semibold text-red-700">
                                        {row.errors.join(', ')}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          Showing all {bulkEditPreview.totalRows} row{bulkEditPreview.totalRows === 1 ? '' : 's'} - scroll to review.
                        </p>
                      </div>
                    )}

                    {bulkEditPreview && (
                      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            setBulkEditPreview(null);
                            setBulkEditFileName('');
                          }}
                          className="rounded-xl bg-gray-100 px-5 py-3 text-sm font-bold text-gray-700 transition hover:bg-gray-200"
                        >
                          Clear upload
                        </button>
                        <button
                          type="button"
                          onClick={handleBulkEditCommit}
                          disabled={bulkEditPreview.invalidRows > 0 || bulkEditCommitMutation.isLoading}
                          className="rounded-xl bg-slate-800 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                        >
                          {bulkEditCommitMutation.isLoading
                            ? 'Applying...'
                            : `Apply changes to ${bulkEditPreview.rows.filter((r) => r.changes.length > 0).length} row${bulkEditPreview.rows.filter((r) => r.changes.length > 0).length === 1 ? '' : 's'}`}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Type Selection Modal */}
      {showFormTypeModal && !editingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full">
            <div className="p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Select Form Type
                </h3>
                <p className="text-gray-600">
                  Choose the type of service form you want to create
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 mb-6">
                <button
                  onClick={() => {
                    setFormType('transfer');
                    setFormData({...formData, isCapped: false}); // Transfer = Uncapped (False)
                    setShowFormTypeModal(false);
                    setShowModal(true);
                  }}
                  className="p-6 border-2 border-blue-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white mr-4 group-hover:scale-110 transition-transform">
                        <i className="fas fa-exchange-alt text-xl"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-1">Transfer</h4>
                        <p className="text-sm text-gray-600">For service transfers - Old Package is required</p>
                      </div>
                    </div>
                    <i className="fas fa-chevron-right text-gray-400 group-hover:text-blue-600 transition-colors"></i>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setFormType('regular');
                    setFormData({...formData, isCapped: true}); // Regular = Capped (True)
                    setShowFormTypeModal(false);
                    setShowModal(true);
                  }}
                  className="p-6 border-2 border-green-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all duration-200 text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center text-white mr-4 group-hover:scale-110 transition-transform">
                        <i className="fas fa-file-alt text-xl"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-1">Regular Form</h4>
                        <p className="text-sm text-gray-600">Standard service form - Old Package is optional</p>
                      </div>
                    </div>
                    <i className="fas fa-chevron-right text-gray-400 group-hover:text-green-600 transition-colors"></i>
                  </div>
                </button>
              </div>

              <button
                onClick={() => {
                  setShowFormTypeModal(false);
                  setFormType(null);
                }}
                className="w-full px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Modal for Create/Edit Form */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto hide-scrollbar">
            {/* Modal Header */}
            <div className="p-8 relative">
              {/* Close button */}
              <button
                onClick={resetForm}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>

              {/* Back button - only show for new forms (not editing) */}
              {!editingForm && formType && (
                <button
                  onClick={() => {
                    setShowModal(false);
                    setShowFormTypeModal(true);
                  }}
                  className="absolute top-4 left-4 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Back to form type selection"
                >
                  <i className="fas fa-arrow-left text-xl"></i>
                </button>
              )}

              <div className={`mb-8 ${!editingForm && formType ? 'mt-12' : ''}`}>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {editingForm ? 'Update Service Form' : 'Create New Service Form'}
                </h3>
                <p className="text-gray-600">
                  {editingForm ? 'Modify customer service details' : `Add a new customer to the monitoring system${formType === 'transfer' ? ' (Transfer)' : formType === 'regular' ? ' (Regular)' : ''}`}
                </p>
              </div>

              {!editingForm && formType && (
                <div className="mb-8 rounded-2xl border border-blue-100 bg-blue-50 p-6">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 flex items-center gap-3">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
                          <i className="fas fa-file-upload"></i>
                        </span>
                        Bulk upload
                      </h4>
                      <p className="mt-2 text-sm text-gray-600">
                        {formType === 'transfer'
                          ? 'Upload transfer customers in Excel or CSV format. Required columns include Customer Name, Customer ID, Service Line, Old Service Line, Old Package, Package Limit, and Activation Date.'
                          : 'Upload regular customers in Excel or CSV format. Required columns include Customer Name, Customer ID, Service Line, Package Limit, and Activation Date. Old Service Line / Old Package are not needed for Regular forms.'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={downloadBulkTemplate}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-bold text-blue-700 ring-1 ring-blue-200 transition hover:bg-blue-100"
                      >
                        <i className="fas fa-download"></i>
                        Template
                      </button>
                      <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-blue-700">
                        <i className="fas fa-upload"></i>
                        Upload sheet
                        <input
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          onChange={handleBulkFileChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>

                  {(bulkPreviewMutation.isLoading || bulkImportFileName || bulkImportPreview) && (
                    <div className="mt-5 rounded-xl bg-white p-4 ring-1 ring-blue-100">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <div className="text-sm font-bold text-gray-900">
                            {bulkImportFileName || 'Preparing preview'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {bulkPreviewMutation.isLoading
                              ? 'Reading and validating rows...'
                              : bulkImportPreview
                                ? `${bulkImportPreview.sheetName} • ${bulkImportPreview.totalRows} row${bulkImportPreview.totalRows === 1 ? '' : 's'}`
                                : 'No preview loaded'}
                          </div>
                        </div>
                        {bulkImportPreview && (
                          <div className="flex flex-wrap gap-2 text-xs font-bold">
                            <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700">
                              Total: {bulkImportPreview.totalRows}
                            </span>
                            <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-700">
                              Valid: {bulkImportPreview.validRows}
                            </span>
                            <span className="rounded-full bg-red-100 px-3 py-1 text-red-700">
                              Errors: {bulkImportPreview.invalidRows}
                            </span>
                          </div>
                        )}
                      </div>

                      {bulkImportPreview && (
                        <div className="mt-4">
                          {/* Scrollable on both axes so a large sheet (100+ rows, or many
                              error rows) can all be reviewed here instead of only the
                              first few - the header stays pinned while the body scrolls. */}
                          <div className="max-h-96 overflow-y-auto overflow-x-auto rounded-lg ring-1 ring-gray-100">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                              <thead className="sticky top-0 z-10 bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left font-bold text-gray-600">Row</th>
                                  <th className="px-3 py-2 text-left font-bold text-gray-600">Customer</th>
                                  <th className="px-3 py-2 text-left font-bold text-gray-600">Service Line</th>
                                  {formType === 'transfer' && (
                                    <th className="px-3 py-2 text-left font-bold text-gray-600">Old Service Line</th>
                                  )}
                                  <th className="px-3 py-2 text-left font-bold text-gray-600">Package</th>
                                  <th className="px-3 py-2 text-left font-bold text-gray-600">Status</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 bg-white">
                                {bulkImportPreview.rows.map((row) => (
                                  <tr key={row.rowNumber}>
                                    <td className="px-3 py-2 font-semibold text-gray-700">{row.rowNumber}</td>
                                    <td className="px-3 py-2 text-gray-700">{row.data.customerName || '-'}</td>
                                    <td className="px-3 py-2 text-gray-700">{row.data.serviceLine || '-'}</td>
                                    {formType === 'transfer' && (
                                      <td className="px-3 py-2 text-gray-700">{row.data.oldServiceLine || '-'}</td>
                                    )}
                                    <td className="px-3 py-2 text-gray-700">{row.data.packageLimit || '-'}</td>
                                    <td className="px-3 py-2">
                                      {row.errors.length === 0 ? (
                                        <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-700">Ready</span>
                                      ) : (
                                        <div className="max-w-xs text-xs font-semibold text-red-700">
                                          {row.errors.join(', ')}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            Showing all {bulkImportPreview.totalRows} row{bulkImportPreview.totalRows === 1 ? '' : 's'} - scroll to review.
                          </p>
                        </div>
                      )}

                      {bulkImportPreview && (
                        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
                          <button
                            type="button"
                            onClick={() => {
                              setBulkImportPreview(null);
                              setBulkImportFileName('');
                            }}
                            className="rounded-xl bg-gray-100 px-5 py-3 text-sm font-bold text-gray-700 transition hover:bg-gray-200"
                          >
                            Clear upload
                          </button>
                          <button
                            type="button"
                            onClick={handleBulkImportCommit}
                            disabled={bulkImportPreview.invalidRows > 0 || bulkCommitMutation.isLoading}
                            className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                          >
                            {bulkCommitMutation.isLoading ? 'Importing...' : `Import ${bulkImportPreview.validRows} ${formType === 'regular' ? 'regular' : 'transfer'} rows`}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Customer Information Section */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-100">
                  <h4 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white mr-3">
                      <i className="fas fa-user"></i>
                    </div>
                    Customer Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Customer Name *
                      </label>
                      <input
                        type="text"
                        value={formData.customerName}
                        onChange={(e) => setFormData({...formData, customerName: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200"
                        required
                        placeholder="Enter customer full name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Customer ID *
                      </label>
                      <input
                        type="text"
                        value={formData.customerId}
                        onChange={(e) => setFormData({...formData, customerId: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200"
                        required
                        placeholder="Enter unique customer ID"
                      />
                    </div>
                  </div>
                </div>

                {/* Service Details Section */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-100">
                  <h4 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center text-white mr-3">
                      <i className="fas fa-satellite"></i>
                    </div>
                    Service Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Service Line *
                      </label>
                      <input
                        type="text"
                        value={formData.serviceLine}
                        onChange={(e) => setFormData({...formData, serviceLine: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all duration-200"
                        required
                        placeholder="Enter service line number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Kit Number
                      </label>
                      <input
                        type="text"
                        value={formData.kitNumber}
                        onChange={(e) => setFormData({...formData, kitNumber: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all duration-200"
                        placeholder="Enter kit number (optional)"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      Old Service Line {!editingForm && formType === 'transfer' && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={formData.oldServiceLine}
                      onChange={(e) => setFormData({...formData, oldServiceLine: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all duration-200"
                      placeholder="Previous service line"
                      required={!editingForm && formType === 'transfer'}
                    />
                    {!editingForm && formType === 'transfer' && (
                      <p className="text-xs text-red-600 mt-1">Required for transfer forms</p>
                    )}
                  </div>
                </div>

                {/* Package Configuration Section */}
                <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-6 rounded-2xl border border-purple-100">
                  <h4 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center text-white mr-3">
                      <i className="fas fa-box"></i>
                    </div>
                    Package Configuration
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Old Package */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Old Package (GB) {!editingForm && formType === 'transfer' && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="number"
                        value={formData.oldPackage}
                        onChange={(e) => setFormData({ ...formData, oldPackage: Number(e.target.value) })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-100 focus:border-purple-500 transition-all duration-200"
                        min="0"
                        step="0.1"
                        placeholder="Previous package size"
                        required={!editingForm && formType === 'transfer'}
                      />
                      {!editingForm && formType === 'transfer' && (
                        <p className="text-xs text-red-600 mt-1">Required for transfer forms</p>
                      )}
                    </div>

                    {/* Capped Package */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Capped Package
                      </label>
                      <select
                        value={selectedCappedPackage || ''}
                        onChange={(e) => {
                          const cap = Number(e.target.value);
                          setSelectedCappedPackage(cap || null);
                          if (!cap) {
                            setProrated(null);
                            setFormData({ ...formData, packageLimit: 0, proratedFromCapGB: null });
                            return;
                          }
                          
                          // Use current date for proration (activation date does not affect proration)
                          const prorateDate = new Date();
                          const currentDay = prorateDate.getDate();
                          
                          // If it's the 1st day of the month, set full package without asking
                          if (currentDay === 1) {
                            setProrated(null);
                            setFormData({ ...formData, packageLimit: cap, proratedFromCapGB: null });
                            return;
                          }
                          
                          // From 2nd day onwards, show proration preview modal
                          const prorate = proratedGB(cap, prorateDate);
                          setPendingPackage({ cap, prorated: prorate, full: cap });
                          setShowProrateModal(true);
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-100 focus:border-purple-500 transition-all duration-200"
                      >
                        <option value="">Select package size…</option>
                        {Array.isArray(packages) && packages.map((p) => (
                          <option key={p.id} value={p.capGB}>
                            {p.capGB} GB/month
                          </option>
                        ))}
                      </select>
                      {prorated !== null && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-700 font-semibold flex items-center">
                            <i className="fas fa-check-circle mr-2"></i>
                            Prorated for this month: {prorated} GB
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Package Limit and Data Topup */}
                  <div className="mt-6 flex gap-4">
                    <div className="w-2/3">
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Package Limit (GB) *
                      </label>
                      {editingForm ? (
                        // Edit mode: Show locked field with edit pencil
                        <div className="relative">
                          <input
                            type="number"
                            value={formData.packageLimit}
                            onChange={(e) => setFormData({ ...formData, packageLimit: Number(e.target.value) })}
                            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-purple-100 focus:border-purple-500 transition-all duration-200 ${
                              isPackageLimitEditable 
                                ? 'border-gray-200 bg-white' 
                                : 'border-gray-300 bg-gray-50 text-gray-600 cursor-not-allowed'
                            }`}
                            required
                            min="0"
                            step="0.1"
                            placeholder="Enter package limit in GB"
                            disabled={!isPackageLimitEditable}
                          />
                          <button
                            type="button"
                            onClick={() => setIsPackageLimitEditable(!isPackageLimitEditable)}
                            className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-2 rounded-lg transition-all duration-200 ${
                              isPackageLimitEditable 
                                ? 'text-green-600 hover:bg-green-100' 
                                : 'text-gray-500 hover:bg-gray-100'
                            }`}
                            title={isPackageLimitEditable ? 'Lock field' : 'Edit field'}
                          >
                            <i className={`fas ${isPackageLimitEditable ? 'fa-lock' : 'fa-edit'} text-sm`}></i>
                          </button>
                        </div>
                      ) : (
                        // Create mode: Show normal input
                        <input
                          type="number"
                          value={formData.packageLimit}
                          onChange={(e) => setFormData({ ...formData, packageLimit: Number(e.target.value) })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-100 focus:border-purple-500 transition-all duration-200"
                          required
                          min="0"
                          step="0.1"
                          placeholder="Enter package limit in GB"
                        />
                      )}
                    </div>

                    <div className="w-1/3">
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Data Topup (GB)
                      </label>
                      <input
                        type="number"
                        value={formData.dataTopup || 0}
                        onChange={(e) => setFormData({ ...formData, dataTopup: Number(e.target.value) })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all duration-200"
                        min="0"
                        step="0.1"
                        placeholder="Enter additional data topup in GB"
                      />
                    </div>
                  </div>
                  <div className="mb-6">
                    <p className="text-xs text-gray-500 mt-2">
                      Additional data purchased by customer (e.g., 250 GB topup)
                    </p>
                  </div>
                </div>

                {/* Additional Information Section */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-2xl border border-orange-100">
                  <h4 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center text-white mr-3">
                      <i className="fas fa-calendar"></i>
                    </div>
                    Additional Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Activation Date *
                      </label>
                      <input
                        type="date"
                        value={formData.activationDate}
                        onChange={(e) => {
                          setFormData({...formData, activationDate: e.target.value});
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Client Status *
                      </label>
                      <select
                        value={formData.isActive ? 'active' : 'inactive'}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      Account Manager
                    </label>
                    <select
                      value={formData.accountManager}
                      onChange={(e) => setFormData({...formData, accountManager: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200"
                    >
                      <option value="">Select Account Manager (optional)</option>
                      {accountManagers.map((manager) => (
                        <option key={manager.id} value={manager.username}>
                          {manager.username} ({manager.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      Capped/Uncapped *
                    </label>
                    <select
                      value={formData.isCapped ? 'capped' : 'uncapped'}
                      onChange={(e) => setFormData({...formData, isCapped: e.target.value === 'capped'})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200"
                      disabled={!editingForm && formType !== null}
                      title={!editingForm && formType !== null ? `Automatically set to ${formType === 'regular' ? 'Capped' : 'Uncapped'} based on form type` : ''}
                    >
                      <option value="capped">Capped (True)</option>
                      <option value="uncapped">Uncapped (False)</option>
                    </select>
                    {!editingForm && formType !== null && (
                      <p className="mt-2 text-sm text-gray-500">
                        {formType === 'regular' ? '✓ Automatically set to Capped for Regular forms' : '✓ Automatically set to Uncapped for Transfer forms'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-4 pt-8 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-8 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-10 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    <i className={`fas ${editingForm ? 'fa-save' : 'fa-plus'} mr-2`}></i>
                    {editingForm ? 'Update' : 'Create'} Form
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Audit Trail Modal */}
      {auditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8 relative">
              {/* Close button */}
              <button
                onClick={() => setAuditModalOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>

              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-1 flex items-center">
                  <i className="fas fa-history mr-3 text-violet-600"></i>
                  Audit Trail
                </h3>
                {auditTargetForm && (
                  <p className="text-gray-600">
                    {auditTargetForm.customerName} – {auditTargetForm.serviceLine}
                  </p>
                )}
              </div>

              {auditLogs.length === 0 ? (
                <p className="text-gray-500">No audit records found.</p>
              ) : (
                <ul className="space-y-6">
                  {auditLogs.map((log) => (
                    <li key={log.id} className="border-l-4 border-violet-500 pl-4">
                      <div className="text-sm text-gray-600 flex items-center mb-1">
                        <i className="fas fa-user mr-2"></i>
                        {log.user?.username || 'System'}
                        <span className="mx-2">•</span>
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                      <div className="text-gray-800">
                        <span className="font-semibold capitalize mr-1">{log.fieldName}</span>
                        <span className="line-through text-red-600 mr-1">{log.oldValue || '—'}</span>
                        <i className="fas fa-arrow-right text-gray-400 mx-1"></i>
                        <span className="text-green-600 font-bold">{log.newValue}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reset TopUp Confirmation Modal */}
      {resetTopupModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full">
            <div className="p-8 relative">
              {/* Close button */}
              <button
                onClick={() => setResetTopupModalOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>

              {/* Warning Icon */}
              <div className="text-center mb-6">
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Reset Data TopUp
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Are you sure you want to reset <strong>ALL</strong> data topup values to 0?
                </p>
                <p className="text-red-600 font-semibold mt-3">
                  This action cannot be undone.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => setResetTopupModalOpen(false)}
                  className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-all duration-200 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetTopup}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <i className="fas fa-undo mr-2"></i>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Service Line Confirmation Modal */}
      {deactivateModalOpen && formToDeactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full transform transition-all">
            <div className="p-8 relative">
              {/* Close button */}
              <button
                onClick={() => {
                  if (!isDeactivating) {
                    setDeactivateModalOpen(false);
                    setFormToDeactivate(null);
                  }
                }}
                disabled={isDeactivating}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-times text-xl"></i>
              </button>

              {/* Icon and Header */}
              <div className="text-center mb-6">
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mb-5 shadow-lg">
                  <i className="fas fa-power-off text-white text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-3">
                  Deactivate Service Line
                </h3>
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-5 mb-4 border border-red-100">
                  <div className="flex items-center justify-center mb-3">
                    <div className="bg-white rounded-xl px-4 py-2 shadow-md">
                      <span className="text-2xl font-bold text-gray-900 font-mono">
                        {formToDeactivate.serviceLine}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-700 font-medium">
                    <i className="fas fa-user text-red-600 mr-2"></i>
                    {formToDeactivate.customerName}
                  </p>
                  {formToDeactivate.customerId && (
                    <p className="text-sm text-gray-600 mt-1">
                      Customer ID: {formToDeactivate.customerId}
                    </p>
                  )}
                </div>
              </div>

              {/* Warning Message */}
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-lg mb-6">
                <div className="flex items-start">
                  <i className="fas fa-exclamation-triangle text-amber-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <p className="text-amber-800 font-semibold mb-2">
                      Important: This action will:
                    </p>
                    <ul className="text-amber-700 text-sm space-y-1 list-disc list-inside">
                      <li>Call the Starlink API to immediately deactivate this service line</li>
                      <li>Update the service status to "Opted Out"</li>
                      <li>Mark the service as inactive in the system</li>
                      <li>Create an audit trail entry for this action</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    if (!isDeactivating) {
                      setDeactivateModalOpen(false);
                      setFormToDeactivate(null);
                    }
                  }}
                  disabled={isDeactivating}
                  className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button
                  onClick={handleDeactivate}
                  disabled={isDeactivating}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isDeactivating ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Deactivating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-power-off mr-2"></i>
                      Confirm Deactivation
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proration Confirmation Modal */}
      {showProrateModal && pendingPackage && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full transform transition-all">
            <div className="p-8 relative">
              {/* Close button */}
              <button
                onClick={() => {
                  setShowProrateModal(false);
                  setPendingPackage(null);
                  // Revert package selection
                  setSelectedCappedPackage(null);
                  setProrated(null);
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>

              {/* Icon and Header */}
              <div className="text-center mb-6">
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-5 shadow-lg">
                  <i className="fas fa-calculator text-white text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-3">
                  Prorate Package?
                </h3>
                <p className="text-gray-600 text-lg mb-4">
                  You've selected a package. Would you like to prorate it for the remaining days of the month?
                </p>
              </div>

              {/* Package Details */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 mb-4 border border-blue-100">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">Full Package:</span>
                    <span className="text-2xl font-bold text-gray-900">{pendingPackage.full} GB</span>
                  </div>
                  <div className="border-t border-blue-200 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Prorated Amount:</span>
                      <span className="text-2xl font-bold text-blue-600">{pendingPackage.prorated} GB</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Based on remaining days in {formData.activationDate ? new Date(formData.activationDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'this month'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    // Use full package (no proration)
                    setFormData({ ...formData, packageLimit: pendingPackage.full, proratedFromCapGB: null });
                    setProrated(null);
                    setShowProrateModal(false);
                    setPendingPackage(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-all duration-200 font-semibold"
                >
                  <i className="fas fa-times mr-2"></i>
                  Use Full Package
                </button>
                <button
                  onClick={() => {
                    // Use prorated amount — mark the package cap it came from
                    // so the monthly reset restores this exact cap.
                    setFormData({ ...formData, packageLimit: pendingPackage.prorated, proratedFromCapGB: pendingPackage.cap });
                    setProrated(pendingPackage.prorated);
                    setShowProrateModal(false);
                    setPendingPackage(null);
                  }}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <i className="fas fa-check mr-2"></i>
                  Yes, Prorate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Service Line Confirmation Modal */}
      {reactivateModalOpen && formToReactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full transform transition-all">
            <div className="p-8 relative">
              {/* Close button */}
              <button
                onClick={() => {
                  if (!isReactivating) {
                    setReactivateModalOpen(false);
                    setFormToReactivate(null);
                  }
                }}
                disabled={isReactivating}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-times text-xl"></i>
              </button>

              {/* Icon and Header */}
              <div className="text-center mb-6">
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-5 shadow-lg">
                  <i className="fas fa-play text-white text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-3">
                  Resume Service Line
                </h3>
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-5 mb-4 border border-green-100">
                  <div className="flex items-center justify-center mb-3">
                    <div className="bg-white rounded-xl px-4 py-2 shadow-md">
                      <span className="text-2xl font-bold text-gray-900 font-mono">
                        {formToReactivate.serviceLine}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-700 font-medium">
                    <i className="fas fa-user text-green-600 mr-2"></i>
                    {formToReactivate.customerName}
                  </p>
                  {formToReactivate.customerId && (
                    <p className="text-sm text-gray-600 mt-1">
                      Customer ID: {formToReactivate.customerId}
                    </p>
                  )}
                </div>
              </div>

              {/* Info Message */}
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-6">
                <div className="flex items-start">
                  <i className="fas fa-info-circle text-blue-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <p className="text-blue-800 font-semibold mb-2">
                      This action will:
                    </p>
                    <ul className="text-blue-700 text-sm space-y-1 list-disc list-inside">
                      <li>Fetch the product reference ID from the Starlink API automatically</li>
                      <li>Call the Starlink API to reactivate this service line</li>
                      <li>Update the service status to "Opted In"</li>
                      <li>Mark the service as active in the system</li>
                      <li>Create an audit trail entry for this action</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    if (!isReactivating) {
                      setReactivateModalOpen(false);
                      setFormToReactivate(null);
                    }
                  }}
                  disabled={isReactivating}
                  className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button
                  onClick={handleReactivate}
                  disabled={isReactivating}
                  className="flex-1 bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isReactivating ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Reactivating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-play mr-2"></i>
                      Confirm Reactivation
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Forms;

// Ensure this file is treated as a module
export {}; 
